import { Component, OnDestroy, OnInit } from '@angular/core';
import { CityDeliveryMap, DeliveryZoneModel, HxDeliveryZoneService, SaveZoneRequest } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { getFormErrors } from '@adm-web-app/utils';
import { formatISO, parseISO } from 'date-fns';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxBrandSelectComponent, HxCitySelectComponent } from 'hx-components';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NgIf } from '@angular/common';
import { CityMapPropertyComponent } from '../city-map/city-map-property.component';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

@Component({
    selector: 'hc-delivery-zone-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxBrandSelectComponent, HxCitySelectComponent, NzDatePickerComponent, NgIf, CityMapPropertyComponent, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe]
})
export class DeliveryZoneEditComponent implements OnInit, OnDestroy {

  model?: DeliveryZoneModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    brandId: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    cityId: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    fromDate: new FormControl<Date | null>(null, [Validators.required]),
    zone: new FormControl<string | null>(null, [Validators.required]),
  });
  isLoading = {
    model: false,
  };
  isSaving = false;
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private modal: NzModalService,
    private deliveryZoneService: HxDeliveryZoneService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
  ) {
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as DeliveryZoneModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }
    this.updateForm();
  }

  async submitForm() {
    if (!this.form.valid) {
      console.log('formErrors', getFormErrors(this.form));
      return;
    }
    const formModel = this.form.value;
    if (!formModel.zone || !formModel.cityId || !formModel.brandId || !formModel.fromDate) {
      throw new Error('required fields');
    }
    const model: SaveZoneRequest = {
      id: this.model?.id,
      brandId: formModel.brandId,
      cityId: formModel.cityId,
      fromDate: formatISO(formModel.fromDate, {representation: 'date'}),
      zone: JSON.parse(formModel.zone) as CityDeliveryMap,
    };
    this.isSaving = true;
    const result = await this.deliveryZoneService.saveZone(model);

    try {
      this.form.markAsPristine();
      this.notification.create(
        'success',
        'Зона доставки',
        this.tr.translate('nt.dataSaved')
      );
      if (this.isCreate) {
        await this.router.navigate([`/delivery-zones/${result.id}/edit`]);
      } else {
        this.isLoading.model = true;
        this.model = await this.deliveryZoneService.getZoneById(result.id);
        this.updateForm();
      }
    } finally {
      this.isLoading.model = false;
      this.isSaving = false;
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  onZoneChanged(zone?: CityDeliveryMap) {
    this.form.controls.zone.setValue(JSON.stringify(zone ?? {}));
    this.form.markAsDirty();
  }

  private updateForm() {
    if (!this.model) {
      return;
    }
    this.form.patchValue({
      brandId: this.model.brand?.id,
      cityId: this.model.city?.id,
      fromDate: parseISO(this.model.fromDate),
      zone: JSON.stringify(this.model.zone),
    });
  }
}
